"use client";

import {
	AnimatePresence,
	motion,
	type Target,
	type TargetAndTransition,
	type Transition,
	type VariantLabels,
} from "motion/react";
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";

import { cn } from "../../lib";

export interface RotatingTextRef {
	next: () => void;
	previous: () => void;
	jumpTo: (index: number) => void;
	reset: () => void;
}

export interface RotatingTextProps
	extends Omit<
		React.ComponentPropsWithoutRef<typeof motion.span>,
		"children" | "transition" | "initial" | "animate" | "exit"
	> {
	texts: string[];
	transition?: Transition;
	initial?: boolean | Target | VariantLabels;
	animate?: boolean | VariantLabels | TargetAndTransition;
	exit?: Target | VariantLabels;
	animatePresenceMode?: "sync" | "wait";
	animatePresenceInitial?: boolean;
	rotationInterval?: number;
	staggerDuration?: number;
	staggerFrom?: "first" | "last" | "center" | "random" | number;
	loop?: boolean;
	auto?: boolean;
	splitBy?: string;
	onNext?: (index: number) => void;
	mainClassName?: string;
	splitLevelClassName?: string;
	elementLevelClassName?: string;
}

interface IntlSegmentIterator {
	segment(input: string): Iterable<{ segment: string }>;
}

function splitIntoCharacters(text: string): string[] {
	const IntlWithSegmenter = Intl as typeof Intl & {
		Segmenter?: new (
			locales: string | undefined,
			options: { granularity: "grapheme" },
		) => IntlSegmentIterator;
	};
	if (typeof Intl !== "undefined" && IntlWithSegmenter.Segmenter) {
		const segmenter = new IntlWithSegmenter.Segmenter("en", {
			granularity: "grapheme",
		});
		return Array.from(segmenter.segment(text), (seg) => seg.segment);
	}
	return Array.from(text);
}

export const RotatingText = forwardRef<RotatingTextRef, RotatingTextProps>(
	function RotatingText(
		{
			texts,
			transition = { type: "spring", damping: 25, stiffness: 300 },
			initial = { y: "100%", opacity: 0 },
			animate = { y: 0, opacity: 1 },
			exit = { y: "-120%", opacity: 0 },
			animatePresenceMode = "wait",
			animatePresenceInitial = false,
			rotationInterval = 2000,
			staggerDuration = 0,
			staggerFrom = "first",
			loop = true,
			auto = true,
			splitBy = "characters",
			onNext,
			mainClassName,
			splitLevelClassName,
			elementLevelClassName,
			...rest
		},
		ref,
	) {
		const [currentTextIndex, setCurrentTextIndex] = useState<number>(0);

		const elements = useMemo(() => {
			const currentText: string = texts[currentTextIndex] ?? "";
			if (splitBy === "characters") {
				const words = currentText.split(" ");
				return words.map((word, i) => ({
					characters: splitIntoCharacters(word),
					needsSpace: i !== words.length - 1,
				}));
			}
			if (splitBy === "words") {
				return currentText.split(" ").map((word, i, arr) => ({
					characters: [word],
					needsSpace: i !== arr.length - 1,
				}));
			}
			if (splitBy === "lines") {
				return currentText.split("\n").map((line, i, arr) => ({
					characters: [line],
					needsSpace: i !== arr.length - 1,
				}));
			}

			return currentText.split(splitBy).map((part, i, arr) => ({
				characters: [part],
				needsSpace: i !== arr.length - 1,
			}));
		}, [texts, currentTextIndex, splitBy]);

		const getStaggerDelay = useCallback(
			(index: number, totalChars: number): number => {
				const total = totalChars;
				if (staggerFrom === "first") {
					return index * staggerDuration;
				}
				if (staggerFrom === "last") {
					return (total - 1 - index) * staggerDuration;
				}
				if (staggerFrom === "center") {
					const center = Math.floor(total / 2);
					return Math.abs(center - index) * staggerDuration;
				}
				if (staggerFrom === "random") {
					const randomIndex = Math.floor(Math.random() * total);
					return Math.abs(randomIndex - index) * staggerDuration;
				}
				return (
					Math.abs((staggerFrom as number) - index) * staggerDuration
				);
			},
			[staggerFrom, staggerDuration],
		);

		const handleIndexChange = useCallback(
			(newIndex: number) => {
				setCurrentTextIndex(newIndex);
				onNext?.(newIndex);
			},
			[onNext],
		);

		const next = useCallback(() => {
			const nextIndex =
				currentTextIndex === texts.length - 1
					? loop
						? 0
						: currentTextIndex
					: currentTextIndex + 1;
			if (nextIndex !== currentTextIndex) {
				handleIndexChange(nextIndex);
			}
		}, [currentTextIndex, texts.length, loop, handleIndexChange]);

		const previous = useCallback(() => {
			const prevIndex =
				currentTextIndex === 0
					? loop
						? texts.length - 1
						: currentTextIndex
					: currentTextIndex - 1;
			if (prevIndex !== currentTextIndex) {
				handleIndexChange(prevIndex);
			}
		}, [currentTextIndex, texts.length, loop, handleIndexChange]);

		const jumpTo = useCallback(
			(index: number) => {
				const validIndex = Math.max(
					0,
					Math.min(index, texts.length - 1),
				);
				if (validIndex !== currentTextIndex) {
					handleIndexChange(validIndex);
				}
			},
			[texts.length, currentTextIndex, handleIndexChange],
		);

		const reset = useCallback(() => {
			if (currentTextIndex !== 0) {
				handleIndexChange(0);
			}
		}, [currentTextIndex, handleIndexChange]);

		useImperativeHandle(
			ref,
			() => ({
				next,
				previous,
				jumpTo,
				reset,
			}),
			[next, previous, jumpTo, reset],
		);

		useEffect(() => {
			if (!auto) {
				return;
			}
			const intervalId = setInterval(next, rotationInterval);
			return () => {
				clearInterval(intervalId);
			};
		}, [next, rotationInterval, auto]);

		const totalCharCount = elements.reduce(
			(sum, word) => sum + word.characters.length,
			0,
		);

		return (
			<motion.span
				className={cn(
					"relative flex flex-wrap whitespace-pre-wrap",
					mainClassName,
				)}
				layout
				transition={transition}
				{...rest}
			>
				<span className="sr-only">{texts[currentTextIndex]}</span>
				<AnimatePresence
					mode={animatePresenceMode}
					initial={animatePresenceInitial}
				>
					<motion.span
						key={currentTextIndex}
						className={cn(
							splitBy === "lines"
								? "relative flex w-full flex-col"
								: "relative flex flex-wrap whitespace-pre-wrap",
						)}
						layout
						aria-hidden="true"
					>
						{elements.map((wordObj, wordIndex, array) => {
							const previousCharsCount = array
								.slice(0, wordIndex)
								.reduce(
									(sum, word) => sum + word.characters.length,
									0,
								);
							return (
								<span
									key={`${currentTextIndex}-${wordIndex}`}
									className={cn(
										"inline-flex",
										splitLevelClassName,
									)}
								>
									{wordObj.characters.map(
										(char, charIndex) => (
											<motion.span
												key={`${currentTextIndex}-${wordIndex}-${charIndex}-${char}`}
												initial={initial}
												animate={animate}
												exit={exit}
												transition={{
													...transition,
													delay: getStaggerDelay(
														previousCharsCount +
															charIndex,
														totalCharCount,
													),
												}}
												className={cn(
													"inline-block",
													elementLevelClassName,
												)}
											>
												{char}
											</motion.span>
										),
									)}
									{wordObj.needsSpace ? (
										<span className="whitespace-pre">
											{" "}
										</span>
									) : null}
								</span>
							);
						})}
					</motion.span>
				</AnimatePresence>
			</motion.span>
		);
	},
);

RotatingText.displayName = "RotatingText";
